import React, { useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from './stores/authStore'
import Layout from './components/Layout'
import LoginPage from './pages/LoginPage'
import UnifiedLogin from './components/UnifiedLogin'
import AuthDebugger from './components/AuthDebugger'
import DevAuthHelper from './components/DevAuthHelper'
import DashboardPage from './pages/DashboardPage'
import UsersPage from './pages/UsersPage'
import UserDetailPage from './pages/UserDetailPage'
import MLSPage from './pages/MLSPage'
import GenerationsPage from './pages/GenerationsPage'
import FinancialPage from './pages/FinancialPage'
import SystemPage from './pages/SystemPage'
import AnalyticsPage from './pages/AnalyticsPage'
import AuditPage from './pages/AuditPage'
import SettingsPage from './pages/SettingsPage'
import WorkflowsPage from './pages/WorkflowsPage'

function App() {
  const { isAuthenticated, verifyMagicLink, user, token, isLoading, error, initializeAuth } = useAuthStore()
  
  // Debug auth state changes
  useEffect(() => {
    console.log('🔍 App.tsx: Auth state changed:', {
      isAuthenticated,
      hasUser: !!user,
      hasToken: !!token,
      isLoading,
      error,
      userEmail: user?.email,
      userRole: user?.role
    })
  }, [isAuthenticated, user, token, isLoading, error])

  // Initialize authentication on app startup
  useEffect(() => {
    console.log('🚀 App.tsx: Initializing authentication...')
    initializeAuth().catch((error) => {
      console.error('❌ App.tsx: Authentication initialization failed:', error)
    })
  }, [initializeAuth])

  // Check for magic link token in URL on app load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const token = urlParams.get('token')
    
    console.log('🔍 App.tsx useEffect: URL params check', {
      hasToken: !!token,
      tokenPreview: token?.substring(0, 10) + '...',
      isAuthenticated,
      currentPath: window.location.pathname,
      currentSearch: window.location.search
    })
    
    if (token && !isAuthenticated) {
      console.log('🔍 App.tsx: Starting magic link verification...')
      verifyMagicLink(token).then((result) => {
        console.log('✅ App.tsx: Magic link verification completed:', result)
        // Clean up URL after successful verification
        window.history.replaceState({}, document.title, window.location.pathname)
        console.log('🔍 App.tsx: URL cleaned up')
      }).catch((error) => {
        console.error('❌ App.tsx: Magic link verification failed:', error)
      })
    } else if (token && isAuthenticated) {
      console.log('🔍 App.tsx: Token found but already authenticated, cleaning URL')
      window.history.replaceState({}, document.title, window.location.pathname)
    } else if (!token) {
      console.log('🔍 App.tsx: No token in URL')
    }
  }, [isAuthenticated, verifyMagicLink])

  console.log('🔍 App.tsx: Render decision:', {
    isAuthenticated,
    hasUser: !!user,
    hasToken: !!token,
    isLoading,
    willShowLogin: !isAuthenticated,
    willShowLayout: isAuthenticated,
    userEmail: user?.email,
    tokenPreview: token?.substring(0, 20) + '...'
  })

  if (!isAuthenticated) {
    return (
      <div>
        <AuthDebugger />
        <UnifiedLogin onLoginSuccess={(user) => {
          console.log('✅ App.tsx: Login successful for user:', user.email)
          console.log('🔍 App.tsx: onLoginSuccess callback executed')
        }} />
      </div>
    )
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/users" element={<UsersPage />} />
        <Route path="/users/:userId" element={<UserDetailPage />} />
        <Route path="/mls" element={<MLSPage />} />
        <Route path="/workflows" element={<WorkflowsPage />} />
        <Route path="/generations" element={<GenerationsPage />} />
        <Route path="/financial" element={<FinancialPage />} />
        <Route path="/system" element={<SystemPage />} />
        <Route path="/analytics" element={<AnalyticsPage />} />
        <Route path="/audit" element={<AuditPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      <DevAuthHelper />
    </Layout>
  )
}

export default App